import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import {
  Play,
  Users,
  RefreshCcw,
  AlertCircle,
  Zap,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  AlertTriangle,
  Sparkles,
  CheckCircle2
} from 'lucide-react';

import AttendanceSidebar from '../components/AttendanceSidebar';
import AttendanceCalendar from '../components/AttendanceCalendar';
import AttendanceDayModal from '../components/AttendanceDayModal';
import PuantajQualityDetailDrawer from '../components/PuantajQualityDetailDrawer';
import DynamicWeekOffModal from '../components/DynamicWeekOffModal';
import BatchCorrectionModal from '../components/BatchCorrectionModal';
import { puantajQualityApi } from '../api/puantajQualityApi';

const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const formatMinutesCompact = (minutes) => {
  if (!minutes) return '0 sa';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${minutes < 0 ? '-' : ''}${h} sa ${m} dk`;
};

const formatLocalYMD = (dateVal) => {
  if (!dateVal) return '';
  if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateVal)) {
    return dateVal.substring(0, 10);
  }
  const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const SummaryPill = ({ label, value, tone = 'default' }) => {
  const toneClasses = {
    default:
      'border-slate-200/90 bg-slate-50/80 text-slate-900 dark:border-slate-800 dark:bg-slate-800/50 dark:text-slate-100',
    danger:
      'border-rose-200/80 bg-rose-50/50 text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/25 dark:text-rose-200',
    success:
      'border-emerald-200/80 bg-emerald-50/50 text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/25 dark:text-emerald-200',
  };
  return (
    <div className={`rounded-xl border px-3 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.02)] ${toneClasses[tone] || toneClasses.default}`}>
      <div className="text-[11px] font-semibold tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm font-bold tabular-nums">{value}</div>
    </div>
  );
};

export default function PuantajControlPage() {
  const { firmCode, isAuthenticated, permissions } = useAuth();

  const canViewReport = useMemo(() => permissions.includes('payroll:view_report'), [permissions]);
  const canCalculate = useMemo(() => permissions.includes('payroll:calculate'), [permissions]);

  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth() + 1);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [qualityFilter, setQualityFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [sidebarLoading, setSidebarLoading] = useState(false);

  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [calcScope, setCalcScope] = useState('selected');

  const [activeEmployee, setActiveEmployee] = useState(null);
  const activeEmployeeIdRef = useRef(null);
  useEffect(() => {
    activeEmployeeIdRef.current = activeEmployee?.employeeId || null;
  }, [activeEmployee]);

  const [calendarData, setCalendarData] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [dayDetail, setDayDetail] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const [qualityDrawerOpen, setQualityDrawerOpen] = useState(false);
  const [qualityDrawerId, setQualityDrawerId] = useState(null);
  const [qualityDrawerData, setQualityDrawerData] = useState(null);

  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchModalDate, setBatchModalDate] = useState(() => formatLocalYMD(new Date()));

  const [weekOffModalOpen, setWeekOffModalOpen] = useState(false);
  const [weekOffActiveDate, setWeekOffActiveDate] = useState(null);
  const [weekOffInitialDates, setWeekOffInitialDates] = useState([]);
  const [weekOffSystemDates, setWeekOffSystemDates] = useState([]);
  const [weekOffDisabledDates, setWeekOffDisabledDates] = useState([]);
  const [weekOffWeeklyRight, setWeekOffWeeklyRight] = useState(1);

  const [calendarWeekSummaries, setCalendarWeekSummaries] = useState([]);
  const [calendarWeeklyRight, setCalendarWeeklyRight] = useState(0);

  const [isCalculating, setIsCalculating] = useState(false);
  const [calcProgress, setCalcProgress] = useState(0);
  const [calcEtaSeconds, setCalcEtaSeconds] = useState(null);
  const [calcProcessed, setCalcProcessed] = useState(0);
  const [calcTotal, setCalcTotal] = useState(0);
  const calcPollRef = useRef(null);

  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => () => {
    if (calcPollRef.current) clearInterval(calcPollRef.current);
  }, []);

  // Arama için 300ms debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Departman listesini yükle
  useEffect(() => {
    if (!isAuthenticated || !firmCode) return;
    let mounted = true;
    apiClient('Department')
      .then((res) => {
        if (!mounted) return;
        const list = Array.isArray(res) ? res : res?.items || [];
        setDepartments(list);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [firmCode, isAuthenticated]);

  const handleOpenQualityDrawer = useCallback(async (puantajId) => {
    if (!puantajId) return;
    try {
      setError(null);
      setQualityDrawerId(puantajId);
      const data = await puantajQualityApi.getDetail(puantajId);
      setQualityDrawerData(data);
      setQualityDrawerOpen(true);
    } catch (err) {
      setError('Puantaj kalite detayı yüklenemedi: ' + (err.message || 'Bilinmeyen hata'));
    }
  }, []);

  const fetchCalendarData = useCallback(async (empId) => {
    if (!empId) return;
    setCalendarLoading(true);
    try {
      const params = new URLSearchParams({ employeeId: empId, month: viewMonth, year: viewYear });
      const res = await apiClient(`payroll/calendar-data?${params}`);
      if (res && Array.isArray(res.days)) {
        setCalendarData(res.days);
        setCalendarWeeklyRight(res.weeklyRight || 0);
        setCalendarWeekSummaries(res.weekSummaries || []);
      } else if (Array.isArray(res)) {
        setCalendarData(res);
        setCalendarWeeklyRight(0);
        setCalendarWeekSummaries([]);
      } else {
        setCalendarData([]);
      }
    } catch (err) {
      setError('Takvim verisi yüklenemedi.');
    } finally {
      setCalendarLoading(false);
    }
  }, [viewMonth, viewYear]);

  const handleSelectEmployee = useCallback((emp) => {
    setActiveEmployee(emp);
    if (emp?.employeeId) {
      fetchCalendarData(emp.employeeId);
    }
  }, [fetchCalendarData]);

  const summaryRequestIdRef = useRef(0);

  const fetchMonthlySummary = useCallback(async (
    pNum = 1,
    sTerm = debouncedSearch,
    qFilter = qualityFilter,
    deptId = selectedDepartmentId
  ) => {
    if (!firmCode) return;
    const reqId = ++summaryRequestIdRef.current;
    setSidebarLoading(true);
    try {
      const params = new URLSearchParams({
        month: viewMonth,
        year: viewYear,
        pageNumber: pNum,
        pageSize,
        ...(sTerm ? { search: sTerm } : {}),
        ...(qFilter ? { qualityFilter: qFilter } : {}),
        ...(deptId ? { departmentId: deptId } : {}),
      });

      const res = await apiClient(`payroll/monthly-summary?${params}`);
      if (reqId !== summaryRequestIdRef.current) return;

      const items = res.items || [];
      setEmployees(items);
      setTotalCount(res.totalCount || 0);

      if (items.length === 0) {
        setActiveEmployee(null);
        setCalendarData([]);
        return;
      }

      const currentActiveId = activeEmployeeIdRef.current;
      const matchedActive = currentActiveId ? items.find((x) => x.employeeId === currentActiveId) : null;
      if (matchedActive) {
        setActiveEmployee(matchedActive);
      } else {
        setActiveEmployee(items[0]);
        fetchCalendarData(items[0].employeeId);
      }
    } catch (err) {
      if (reqId === summaryRequestIdRef.current) {
        setError('Personel özetleri yüklenemedi.');
      }
    } finally {
      if (reqId === summaryRequestIdRef.current) {
        setSidebarLoading(false);
      }
    }
  }, [firmCode, viewMonth, viewYear, pageSize, debouncedSearch, qualityFilter, selectedDepartmentId, fetchCalendarData]);

  // Hem sol özet listesini (ve aktif personel KPI kartlarını) hem de takvimi tazeleyen ortak yardımcı
  const refreshActiveAndSummary = useCallback(async () => {
    await fetchMonthlySummary(pageNumber, debouncedSearch, qualityFilter, selectedDepartmentId);
    if (activeEmployeeIdRef.current) {
      await fetchCalendarData(activeEmployeeIdRef.current);
    }
  }, [fetchMonthlySummary, fetchCalendarData, pageNumber, debouncedSearch, qualityFilter, selectedDepartmentId]);

  const fetchDayDetail = async (dateStr) => {
    if (!activeEmployee) return;
    setIsModalOpen(true);
    setModalLoading(true);
    try {
      const cleanDate = formatLocalYMD(dateStr);
      const params = new URLSearchParams({ employeeId: activeEmployee.employeeId, date: cleanDate });
      const data = await apiClient(`payroll/day-detail?${params}`);
      setDayDetail(data);
    } catch (err) {
      setError('Gün detayı yüklenemedi.');
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && firmCode) {
      fetchMonthlySummary(1, debouncedSearch, qualityFilter, selectedDepartmentId);
    }
  }, [firmCode, isAuthenticated, viewMonth, viewYear, pageSize, debouncedSearch, qualityFilter, selectedDepartmentId]);

  const handleSearch = (term) => {
    setSearch(term);
    setPageNumber(1);
  };

  const handlePrevMonth = () => {
    if (viewMonth === 1) {
      setViewMonth(12);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
    setPageNumber(1);
  };

  const handleNextMonth = () => {
    if (viewMonth === 12) {
      setViewMonth(1);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
    setPageNumber(1);
  };

  // Aktif personelin takvimindeki ilk riskli günü bul (hızlı çözüm butonu için)
  const firstRiskyCalendarDay = useMemo(() => {
    if (!calendarData || calendarData.length === 0) return null;
    return (
      calendarData.find((d) => d.qualityStatus === 2 && d.puantajId) ||
      calendarData.find((d) => d.qualityStatus === 1 && d.puantajId) ||
      null
    );
  }, [calendarData]);

  const handleOpenBatchCenter = () => {
    if (firstRiskyCalendarDay?.date) {
      setBatchModalDate(formatLocalYMD(firstRiskyCalendarDay.date));
    } else {
      const now = new Date();
      if (now.getFullYear() === viewYear && now.getMonth() + 1 === viewMonth) {
        setBatchModalDate(formatLocalYMD(now));
      } else {
        setBatchModalDate(`${viewYear}-${String(viewMonth).padStart(2, '0')}-01`);
      }
    }
    setBatchModalOpen(true);
  };

  const handleCalculate = async () => {
    if (!canCalculate) { setError('Hesaplama yetkiniz yok.'); return; }
    if (isCalculating) return;

    const ids = calcScope === 'selected' ? selectedEmployeeIds : null;
    if (calcScope === 'selected' && (!ids || ids.length === 0)) {
      setError('Lütfen en az bir personel seçin.');
      return;
    }

    setIsCalculating(true);
    setCalcProgress(0);
    setCalcEtaSeconds(null);
    setCalcProcessed(0);
    setCalcTotal(0);
    setMessage('Hesaplama kuyruğa alınıyor...');
    setError(null);

    let finished = false;

    const stopPolling = () => {
      if (calcPollRef.current) {
        clearInterval(calcPollRef.current);
        calcPollRef.current = null;
      }
    };

    const finishSuccess = async () => {
      if (finished) return;
      finished = true;
      stopPolling();
      setCalcProgress(100);
      setMessage('Hesaplama tamamlandı ✓');
      try {
        await refreshActiveAndSummary();
      } finally {
        setTimeout(() => {
          setMessage('');
          setIsCalculating(false);
          setCalcProgress(0);
          setCalcEtaSeconds(null);
          setCalcProcessed(0);
          setCalcTotal(0);
        }, 1800);
      }
    };

    const finishFailed = (errMsg) => {
      if (finished) return;
      finished = true;
      stopPolling();
      setError(errMsg || 'Hesaplama başarısız oldu.');
      setMessage('');
      setIsCalculating(false);
      setCalcProgress(0);
      setCalcEtaSeconds(null);
    };

    try {
      const startRes = await apiClient('payroll/calculate', {
        method: 'POST',
        body: {
          employeeIds: ids,
          startDate: `${viewYear}-${String(viewMonth).padStart(2, '0')}-01`,
          endDate: `${viewYear}-${String(viewMonth).padStart(2, '0')}-${String(new Date(viewYear, viewMonth, 0).getDate()).padStart(2, '0')}`,
        },
      }, firmCode);

      const startedRunId = startRes?.runId;
      setMessage('Hesaplama devam ediyor...');

      const pollOnce = async () => {
        try {
          const status = await apiClient('PayrollSettings/status');
          const pct = Math.max(0, Math.min(100, Number(status?.progressPercent) || 0));
          const processed = Number(status?.processedEmployees) || 0;
          const total = Number(status?.totalEmployees) || 0;

          if (startedRunId && status?.runId && status.runId !== startedRunId && status?.isRunning) {
            return;
          }

          if (status?.isRunning) {
            setCalcProgress(pct);
            setCalcProcessed(processed);
            setCalcTotal(total);
            setCalcEtaSeconds(status?.etaSeconds ?? null);
            if (total > 0) {
              setMessage(`Hesaplanıyor: ${processed} / ${total} personel`);
            } else {
              setMessage('Hesaplama devam ediyor...');
            }
            return;
          }

          if (status?.lastStatus === 'Failed') {
            finishFailed(status?.errorMessage || 'Hesaplama başarısız oldu.');
            return;
          }

          if (status?.lastStatus === 'Completed') {
            await finishSuccess();
            return;
          }

          setMessage('Hesaplama başlatılıyor...');
        } catch (pollErr) {
          console.warn('Payroll status poll failed:', pollErr);
        }
      };

      await pollOnce();
      calcPollRef.current = setInterval(pollOnce, 1500);
    } catch (err) {
      stopPolling();
      setError(err.message || 'Hesaplama başlatılamadı.');
      setIsCalculating(false);
      setCalcProgress(0);
      setCalcEtaSeconds(null);
    }
  };

  const handleSelectAllOnPage = (checked) => {
    const idsOnPage = employees.map((e) => e.employeeId);
    if (checked) {
      setSelectedEmployeeIds((prev) => [...new Set([...prev, ...idsOnPage])]);
    } else {
      setSelectedEmployeeIds((prev) => prev.filter((id) => !idsOnPage.includes(id)));
    }
  };

  if (!canViewReport) {
    return <div className="p-8 text-center font-bold text-red-500">Yetki Hatası: Rapor görüntüleme yetkiniz bulunmamaktadır.</div>;
  }

  return (
    <div className="min-h-screen w-full bg-slate-50 p-6 dark:bg-gray-900">
      <div className="w-full space-y-5">
        {/* Üst Araç Çubuğu */}
        <div className="rounded-3xl border border-slate-200/70 bg-white px-5 py-2.5 shadow-lg shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
          <div className="flex flex-wrap items-center gap-3 xl:flex-nowrap">
            {/* Hızlı Ay/Yıl Seçici + Oklar */}
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                title="Önceki Ay"
              >
                <ChevronLeft size={16} />
              </button>

              <select
                value={viewMonth}
                onChange={(e) => {
                  setViewMonth(parseInt(e.target.value, 10));
                  setPageNumber(1);
                }}
                className="h-9 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-700 outline-none transition-all focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                {monthNames.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>

              <select
                value={viewYear}
                onChange={(e) => {
                  setViewYear(parseInt(e.target.value, 10));
                  setPageNumber(1);
                }}
                className="h-9 w-24 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-700 outline-none transition-all focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                {Array.from(new Set([
                  ...Array.from({ length: 11 }, (_, idx) => new Date().getFullYear() - 5 + idx),
                  viewYear
                ])).sort((a, b) => a - b).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              <button
                type="button"
                onClick={handleNextMonth}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                title="Sonraki Ay"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <select
              value={calcScope}
              onChange={(e) => setCalcScope(e.target.value)}
              className="h-9 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none transition-all focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <option value="selected">Seçili Personelleri Hesapla ({selectedEmployeeIds.length})</option>
              <option value="all">Tüm Personelleri Hesapla</option>
            </select>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleCalculate}
                disabled={isCalculating}
                className="inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-xl bg-indigo-600 px-5 text-sm font-bold text-white shadow-md shadow-indigo-500/25 transition hover:bg-indigo-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isCalculating ? (
                  <><div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" /> Hesaplanıyor</>
                ) : (
                  <><Play size={14} fill="currentColor" /> Hesapla</>
                )}
              </button>

              <button
                type="button"
                onClick={handleOpenBatchCenter}
                className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-xl border border-amber-300/80 bg-amber-50 px-3.5 text-xs font-bold text-amber-800 shadow-sm transition hover:bg-amber-100 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-900/50"
                title="Günlük puantaj risklerini ve izin çakışmalarını toplu analiz et ve düzelt"
              >
                <Zap size={14} className="text-amber-600 dark:text-amber-400" />
                Toplu Risk & Kalite Merkezi
              </button>

              <button
                type="button"
                onClick={() => refreshActiveAndSummary()}
                disabled={isCalculating}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                title="Listeyi ve takvimi yenile"
              >
                <RefreshCcw size={16} className={sidebarLoading || calendarLoading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
        </div>

        {/* Progress bar — Hangfire PayrollRun ile senkron */}
        {isCalculating && (
          <div className="overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-sm dark:border-indigo-900/40 dark:bg-slate-900">
            <div className="flex items-center justify-between px-5 py-2.5">
              <div className="flex items-center gap-2.5">
                <div className="h-2 w-2 animate-pulse rounded-full bg-indigo-500" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {message || 'Hesaplama devam ediyor...'}
                </span>
                {calcEtaSeconds != null && calcEtaSeconds > 0 && (
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    · ~{calcEtaSeconds < 60 ? `${calcEtaSeconds} sn` : `${Math.ceil(calcEtaSeconds / 60)} dk`} kaldı
                  </span>
                )}
              </div>
              <span className="text-sm font-bold tabular-nums text-indigo-600 dark:text-indigo-400">
                %{calcProgress}
                {calcTotal > 0 ? ` (${calcProcessed}/${calcTotal})` : ''}
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800">
              <div
                className="h-1.5 rounded-full bg-indigo-500 transition-all duration-500 ease-out"
                style={{ width: `${Math.max(calcProgress, calcProgress === 0 ? 2 : calcProgress)}%` }}
              />
            </div>
          </div>
        )}

        {/* Error display */}
        {error && (
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-3 rounded-2xl border border-rose-100 bg-rose-50/90 px-4 py-2 text-xs font-semibold text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300">
              <AlertCircle size={16} />
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold hover:bg-rose-100 dark:hover:bg-rose-900/40"
              >
                Kapat
              </button>
            </div>
          </div>
        )}

        {/* Non-calculating message */}
        {!isCalculating && message && (
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50/90 px-4 py-2 text-xs font-semibold text-blue-700 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-300">
              <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              {message}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
          <AttendanceSidebar
            employees={employees}
            loading={sidebarLoading}
            pagination={{ pageNumber, totalCount, totalPages: Math.ceil(totalCount / pageSize) || 1 }}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNumber(1);
            }}
            onPageChange={(p) => {
              setPageNumber(p);
              fetchMonthlySummary(p, debouncedSearch, qualityFilter, selectedDepartmentId);
            }}
            onSearch={handleSearch}
            selectedIds={selectedEmployeeIds}
            onToggleSelect={(id) => setSelectedEmployeeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))}
            onSelectAll={handleSelectAllOnPage}
            activeEmployeeId={activeEmployee?.employeeId}
            onSelectEmployee={handleSelectEmployee}
            monthLabel={`${monthNames[viewMonth - 1]} ${viewYear}`}
            qualityFilter={qualityFilter}
            onQualityFilterChange={(qf) => {
              setQualityFilter(qf);
              setPageNumber(1);
            }}
            departments={departments}
            selectedDepartmentId={selectedDepartmentId}
            onDepartmentChange={(deptId) => {
              setSelectedDepartmentId(deptId);
              setPageNumber(1);
            }}
          />

          <div className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-lg shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
            {activeEmployee ? (
              <div className="flex flex-col">
                {/* Right panel header — kompakt + Kalite/Risk Aksiyonu */}
                <div className="border-b border-slate-200/70 px-5 py-3.5 dark:border-slate-800">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2.5">
                        <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">{activeEmployee.fullName}</h2>
                        <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-mono font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          #{activeEmployee.employeeId}
                        </span>
                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          · {monthNames[viewMonth - 1]} {viewYear}
                        </span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
                        <span>{activeEmployee.departmentName || activeEmployee.unitName || 'Birim Atanmamış'}</span>
                        {(activeEmployee.qualityErrorCount > 0 || activeEmployee.qualityWarningCount > 0) ? (
                          <div className="flex items-center gap-1.5">
                            {activeEmployee.qualityErrorCount > 0 && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                                <ShieldAlert size={11} /> {activeEmployee.qualityErrorCount} Kritik Hata
                              </span>
                            )}
                            {activeEmployee.qualityWarningCount > 0 && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                <AlertTriangle size={11} /> {activeEmployee.qualityWarningCount} Uyarı
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                            <CheckCircle2 size={11} /> Kalite Kontrolü Temiz
                          </span>
                        )}
                      </div>
                    </div>

                    {firstRiskyCalendarDay?.puantajId && (
                      <button
                        type="button"
                        onClick={() => handleOpenQualityDrawer(firstRiskyCalendarDay.puantajId)}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-rose-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm shadow-rose-500/20 transition hover:bg-rose-700 active:scale-95"
                      >
                        <Sparkles size={13} />
                        İlk Riskli Günü Çöz ({new Date(firstRiskyCalendarDay.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })})
                      </button>
                    )}
                  </div>

                  {/* Monthly KPI strip */}
                  <div className="mt-3 grid grid-cols-3 gap-2 xl:grid-cols-6">
                    <SummaryPill
                      label="Eksik Süre"
                      value={formatMinutesCompact(activeEmployee.totalUndertimeMinutes)}
                      tone={activeEmployee.totalUndertimeMinutes > 0 ? 'danger' : 'default'}
                    />
                    <SummaryPill
                      label="Fazla Mesai"
                      value={formatMinutesCompact(activeEmployee.totalOvertimeMinutes)}
                      tone={activeEmployee.totalOvertimeMinutes > 0 ? 'success' : 'default'}
                    />
                    <SummaryPill label="Toplam Çalışma" value={formatMinutesCompact(activeEmployee.totalActualMinutes)} />
                    <SummaryPill label="Çalışılan Gün" value={String(activeEmployee.workedDayCount || 0)} />
                    <SummaryPill label="Hafta Tatili" value={String(activeEmployee.weekOffCount || 0)} />
                    <SummaryPill label="Geç / Erken" value={formatMinutesCompact((activeEmployee.totalLateEntryMinutes || 0) + (activeEmployee.totalEarlyExitMinutes || 0))} />
                  </div>
                </div>

                {/* Calendar area */}
                <div className="p-5 pt-4">
                  <AttendanceCalendar
                    month={viewMonth}
                    year={viewYear}
                    data={calendarData}
                    weekSummaries={calendarWeekSummaries}
                    weeklyRight={calendarWeeklyRight}
                    loading={calendarLoading}
                    onDayClick={(date) => fetchDayDetail(date)}
                    onOpenQualityDrawer={handleOpenQualityDrawer}
                    onOpenWeekOffModal={(weekStart) => {
                      const weekStartStr = formatLocalYMD(weekStart);
                      const ws = calendarWeekSummaries.find((s) => formatLocalYMD(s.weekStart) === weekStartStr);
                      setWeekOffActiveDate(weekStart);
                      setWeekOffInitialDates(ws?.manualDates || []);
                      setWeekOffSystemDates(ws?.systemDates || []);
                      setWeekOffDisabledDates(ws?.disabledDates || []);
                      setWeekOffWeeklyRight(ws?.weeklyRight || calendarWeeklyRight || 1);
                      setWeekOffModalOpen(true);
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex min-h-[480px] flex-col items-center justify-center gap-4 text-center text-slate-400 dark:text-slate-500">
                <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-dashed border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                  <Users size={38} className="opacity-30" />
                </div>
                <div>
                  <div className="text-sm font-bold uppercase tracking-[0.2em]">Personel Seçiniz</div>
                  <div className="mt-2 text-xs italic">Soldaki listeden personel seçince aylık takvim burada açılır.</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AttendanceDayModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        data={dayDetail} 
        loading={modalLoading} 
        onOpenQualityDrawer={handleOpenQualityDrawer}
      />

      <PuantajQualityDetailDrawer
        isOpen={qualityDrawerOpen}
        onClose={() => {
          setQualityDrawerOpen(false);
          refreshActiveAndSummary();
        }}
        puantajId={qualityDrawerId}
        data={qualityDrawerData}
        firmCode={firmCode}
        onRefresh={async () => {
          await refreshActiveAndSummary();
        }}
      />

      <BatchCorrectionModal
        isOpen={batchModalOpen}
        onClose={() => {
          setBatchModalOpen(false);
          refreshActiveAndSummary();
        }}
        initialDate={batchModalDate}
        onDateChange={(d) => setBatchModalDate(d)}
        onApplied={() => {
          refreshActiveAndSummary();
        }}
      />

      <DynamicWeekOffModal
        isOpen={weekOffModalOpen}
        onClose={() => {
          setWeekOffModalOpen(false);
          refreshActiveAndSummary();
        }}
        employeeId={activeEmployee?.employeeId}
        weekStartDate={weekOffActiveDate}
        initialSelectedDates={weekOffInitialDates}
        systemAssignedDates={weekOffSystemDates}
        disabledDates={weekOffDisabledDates}
        weeklyRight={weekOffWeeklyRight}
        firmCode={firmCode}
        onRefresh={() => {
          refreshActiveAndSummary();
        }}
      />
    </div>
  );
}
